import {
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  renameSync,
  statSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { dirname } from 'node:path';

const CREDENTIALS_VERSION = 3;
const STALE_LOCK_AGE_MS = 30_000;

export interface StoredCredentials {
  authToken: string;
  pairingKey: string;
  clientId?: string;
  clientName?: string;
  aliases?: string[];
  createdAt?: string;
}

export interface StoredOnlineCredentials {
  apiKey: string;
  createdAt?: string;
}

interface CredentialsFileV3 {
  version: typeof CREDENTIALS_VERSION;
  devices: Record<string, StoredCredentials>;
  online?: StoredOnlineCredentials;
}

interface CredentialData {
  devices: Record<string, StoredCredentials>;
  online?: StoredOnlineCredentials;
}

interface MutationResult<T> {
  value: T;
  changed: boolean;
}

type LegacyCredentialsFile = Record<string, StoredCredentials>;

function validCredentials(value: unknown): value is StoredCredentials {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<StoredCredentials>;
  return (
    typeof entry.authToken === 'string' &&
    entry.authToken.length > 0 &&
    typeof entry.pairingKey === 'string' &&
    /^[0-9a-f]{64}$/i.test(entry.pairingKey)
  );
}

function validOnlineCredentials(value: unknown): value is StoredOnlineCredentials {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<StoredOnlineCredentials>;
  return typeof entry.apiKey === 'string' && validOnlineApiKey(entry.apiKey);
}

export function validOnlineApiKey(value: string): boolean {
  return /^zpk1_\S+$/.test(value);
}

export class CredentialStore {
  constructor(private readonly path: string) {}

  getCredentials(deviceId: string, aliases: string[] = []): StoredCredentials | undefined {
    const all = this.load().devices;
    for (const candidate of [deviceId, ...aliases]) {
      if (all[candidate]) return all[candidate];
      const matched = Object.values(all).find((entry) => entry.aliases?.includes(candidate));
      if (matched) return matched;
    }
    return undefined;
  }

  saveCredentials(
    deviceId: string,
    authToken: string,
    pairingKey: Uint8Array,
    metadata: Pick<StoredCredentials, 'clientId' | 'clientName' | 'aliases'> = {},
  ): void {
    if (pairingKey.length !== 32) throw new Error('Pairing key must be 32 bytes');
    this.mutate((data) => {
      data.devices[deviceId] = {
        authToken,
        pairingKey: Buffer.from(pairingKey).toString('hex'),
        clientId: metadata.clientId,
        clientName: metadata.clientName,
        aliases: metadata.aliases,
        createdAt: data.devices[deviceId]?.createdAt ?? new Date().toISOString(),
      };
      return { value: undefined, changed: true };
    });
  }

  addAlias(deviceId: string, alias: string): void {
    this.mutate((data) => {
      const credentials = data.devices[deviceId];
      if (!credentials) throw new Error(`No credentials for ${deviceId}`);
      credentials.aliases = [...new Set([...(credentials.aliases ?? []), alias])];
      return { value: undefined, changed: true };
    });
  }

  deleteCredentials(deviceId: string): boolean {
    return this.mutate((data) => {
      const directKey =
        deviceId in data.devices
          ? deviceId
          : Object.keys(data.devices).find((key) => data.devices[key].aliases?.includes(deviceId));
      if (!directKey) return { value: false, changed: false };
      delete data.devices[directKey];
      return { value: true, changed: true };
    });
  }

  listCredentials(): Record<string, StoredCredentials> {
    return this.load().devices;
  }

  pairingKeyBytes(deviceId: string): Uint8Array | undefined {
    const credentials = this.getCredentials(deviceId);
    if (!credentials) return undefined;
    return Buffer.from(credentials.pairingKey, 'hex');
  }

  getOnlineApiKey(): string | undefined {
    return this.load().online?.apiKey;
  }

  saveOnlineApiKey(apiKey: string): void {
    if (!validOnlineApiKey(apiKey)) {
      throw new Error('Online User API key must begin with zpk1_ and contain no whitespace');
    }
    this.mutate((data) => {
      data.online = {
        apiKey,
        createdAt: data.online?.createdAt ?? new Date().toISOString(),
      };
      return { value: undefined, changed: true };
    });
  }

  deleteOnlineApiKey(): boolean {
    return this.mutate((data) => {
      if (!data.online) return { value: false, changed: false };
      delete data.online;
      return { value: true, changed: true };
    });
  }

  private load(): CredentialData {
    if (!existsSync(this.path)) return { devices: {} };
    chmodSync(this.path, 0o600);
    let parsed: unknown;
    try {
      parsed = JSON.parse(readFileSync(this.path, 'utf8'));
    } catch (error) {
      throw new Error(
        `Credentials file is malformed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Credentials file must be an object');
    }

    const candidate = parsed as {
      version?: number;
      devices?: Record<string, StoredCredentials>;
      online?: StoredOnlineCredentials;
    };
    let data: CredentialData;
    if ('version' in candidate) {
      if (candidate.version === CREDENTIALS_VERSION) {
        data = {
          devices: candidate.devices ?? {},
          online: candidate.online,
        };
      } else if (candidate.version === 2) {
        data = { devices: candidate.devices ?? {} };
      } else {
        throw new Error(`Unsupported credentials file version ${String(candidate.version)}`);
      }
    } else {
      data = { devices: parsed as LegacyCredentialsFile };
    }

    for (const [deviceId, credentials] of Object.entries(data.devices)) {
      if (!validCredentials(credentials)) {
        throw new Error(`Invalid credentials entry for ${deviceId}`);
      }
    }
    if (data.online !== undefined && !validOnlineCredentials(data.online)) {
      throw new Error('Invalid Online User API credentials entry');
    }
    return data;
  }

  private mutate<T>(mutation: (data: CredentialData) => MutationResult<T>): T {
    const dir = dirname(this.path);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true, mode: 0o700 });
    const lockPath = `${this.path}.lock`;
    let locked = false;
    try {
      try {
        writeFileSync(lockPath, '', { mode: 0o600, flag: 'wx' });
      } catch (error) {
        if ((error as NodeJS.ErrnoException).code !== 'EEXIST') throw error;
        const lockAgeMs = Date.now() - statSync(lockPath).mtimeMs;
        if (lockAgeMs <= STALE_LOCK_AGE_MS) throw error;
        unlinkSync(lockPath);
        writeFileSync(lockPath, '', { mode: 0o600, flag: 'wx' });
      }
      locked = true;
      const data = this.load();
      const result = mutation(data);
      if (result.changed) this.write(data);
      return result.value;
    } finally {
      if (locked && existsSync(lockPath)) unlinkSync(lockPath);
    }
  }

  private write(data: CredentialData): void {
    const dir = dirname(this.path);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true, mode: 0o700 });
    const tempPath = `${this.path}.tmp-${process.pid}`;
    const payload: CredentialsFileV3 = {
      version: CREDENTIALS_VERSION,
      devices: data.devices,
      online: data.online,
    };
    try {
      writeFileSync(tempPath, JSON.stringify(payload, null, 2), { mode: 0o600 });
      renameSync(tempPath, this.path);
      chmodSync(this.path, 0o600);
    } catch (error) {
      if (existsSync(tempPath)) unlinkSync(tempPath);
      throw error;
    }
  }
}
